import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import CreateSelectEntity from "@/components/CreateSelectEntity";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";

import {ExerciseAPI} from "@/api/exercise";

import {ERROR_OPERATION, REQUIRED_FIELD} from "@/utils/constants";
import {
	checkRequiredFields,
	getTextField,
	getTextAreaField,
} from "@/utils/functions";

export const phasesOptions = [
	{value: 1, label: "Momento 1"},
	{value: 2, label: "Momento 2"},
	{value: 3, label: "Momento 3"},
	{value: 4, label: "Momento 4"},
];

function HandleExerciseModal(props) {
	const {open, confirm, close, initialData, handleNotification} = props;

	const {t} = useTranslation();

	const [exercises, setExercises] = useState([]);
	const [saveOption, setSaveOption] = useState("select");
	const [formData, setFormData] = useState({
		currentExercise: null,
	});
	const [errors, setErrors] = useState([]);

	const getExercises = async () => {
		try {
			const response = await ExerciseAPI.list();
			setExercises(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	useEffect(() => {
		getExercises();
	}, []);

	useEffect(() => {
		if (initialData && exercises?.length) {
			initialData.currentPhase = phasesOptions?.find(
				(p) => p.value === parseInt(initialData.phase),
			);

			initialData.currentExercise = exercises
				?.map((e) => ({value: e.id, label: e.name}))
				?.find((e) => e.value === initialData.exercise.id);

			setFormData(initialData);
		}
	}, [initialData, exercises]);

	useEffect(() => {
		if (!open || saveOption === "new") {
			setFormData({});
		}
	}, [open, saveOption]);

	const defaultTextField = {
		values: formData,
		setValues: setFormData,
		errors,
		setErrors,
	};

	const requiredFields =
		saveOption === "select"
			? ["currentPhase", "currentExercise"]
			: ["currentPhase", "name", "exerciseType", "duration"];

	return (
		<VLFModal
			maxWidth="md"
			open={open}
			title={initialData ? "Modifica informazione" : "Aggiungi informazione"}
			close={close}
			confirm={() => {
				const errors = checkRequiredFields(requiredFields, formData);
				setErrors(errors);

				if (!errors.length) {
					const data =
						saveOption === "new"
							? {...formData, currentExercise: undefined}
							: formData;
					confirm(data, initialData?.id);
				}
			}}
			content={
				<div>
					<div className="row no-margin">
						<div className="col-12 padding-small">
							{!initialData ? (
								<CreateSelectEntity
									currentOption={saveOption}
									setCurrentOption={setSaveOption}
								/>
							) : null}
						</div>
						<div className="col-12 padding-small">
							<VLFSelectNew
								options={phasesOptions}
								value={formData.currentPhase || ""}
								onChange={(value) => {
									const current = exercises?.filter(
										(ex) => ex.id === value?.value,
									)?.[0];

									setErrors([]);
									setFormData({
										...formData,
										currentPhase: value,
										...current,
									});
								}}
								required
								label="Momento"
							/>
						</div>
						{saveOption === "select" ? (
							<div className="col-12 col-lg-6 padding-small">
								<VLFSelectNew
									options={exercises.map((r) => {
										return {
											value: r.id,
											label: r.name,
										};
									})}
									value={formData.currentExercise || ""}
									onChange={(value) => {
										const current = exercises?.filter(
											(ex) => ex.id === value?.value,
										)?.[0];

										delete current.id;

										setErrors([]);
										setFormData({
											...formData,
											currentExercise: value,
											...current,
										});
									}}
									required
									helperText={
										errors.indexOf("currentExercise") !== -1
											? REQUIRED_FIELD
											: ""
									}
									error={errors.indexOf("currentExercise") !== -1}
									label="Esercizio"
								/>
							</div>
						) : (
							<>
								<div className="col-12 col-lg-6 padding-small">
									{getTextField({
										...defaultTextField,
										fieldKey: "name",
										label: t("name"),
										required: true,
									})}
								</div>
								<div className="col-12 col-lg-6 padding-small">
									{getTextField({
										...defaultTextField,
										fieldKey: "exerciseType",
										label: "Tipologia",
										required: true,
									})}
								</div>
								<div className="col-12 padding-small">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "materials",
										label: "Materiale",
										minRows: 5,
										maxRows: 10,
									})}
								</div>
								<div className="col-12 padding-small">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "preparation",
										label: "Preparazione",
										minRows: 5,
										maxRows: 10,
									})}
								</div>
							</>
						)}
						<div className="col-12 col-lg-6 padding-small">
							{getTextField({
								...defaultTextField,
								fieldKey: "duration",
								label: "Tempo di svolgimento (minuti)",
								type: "number",
								required: true,
							})}
						</div>
						{saveOption === "new" ? (
							<div className="col-12 col-lg-6 padding-small">
								{getTextField({
									...defaultTextField,
									fieldKey: "objectives",
									label: "Obiettivi",
								})}
							</div>
						) : null}
						<div className="col-12 padding-small">
							{getTextAreaField({
								...defaultTextField,
								fieldKey: "description",
								label: "Descrizione",
								minRows: 5,
								maxRows: 10,
							})}
						</div>
						{saveOption === "new" ? (
							<div className="col-12 padding-small">
								{getTextAreaField({
									...defaultTextField,
									fieldKey: "rules",
									label: "Regole",
									minRows: 5,
									maxRows: 10,
								})}
							</div>
						) : null}
						<div className="col-12 padding-small">
							{getTextAreaField({
								...defaultTextField,
								fieldKey: "notes",
								label: "Note",
								minRows: 5,
								maxRows: 10,
							})}
						</div>
					</div>
				</div>
			}
		/>
	);
}

export default HandleExerciseModal;
